import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

function ProjectGallery({ images, title }) {
  const [index, setIndex] = useState(0);
  const multi = images.length > 1;

  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  useEffect(() => {
    if (!multi) return undefined;
    const id = setInterval(() => {
      setIndex((current) => (current + 1) % images.length);
    }, 4200);
    return () => clearInterval(id);
  }, [images, images.length, multi]);

  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-[1.25rem] border border-ink/12 bg-snow md:aspect-[16/10] md:rounded-[1.55rem]">
      {images.map((src, i) => (
        <img
          key={`${title}-${i}`}
          src={src}
          alt={`${title} screen ${i + 1}`}
          decoding="async"
          loading={i === 0 ? 'eager' : 'lazy'}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ zIndex: i === index ? 2 : 1 }}
        />
      ))}

      <div className="pointer-events-none absolute inset-0 z-[3] bg-gradient-to-t from-black/40 via-transparent to-transparent" />

      {multi && (
        <div className="absolute bottom-3 left-3 z-10 flex gap-1.5">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIndex(i);
              }}
              aria-label={`Show ${title} screen ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === index ? 'w-8 bg-white' : 'w-3 bg-white/45 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}

      <div className="absolute right-3 top-3 z-10 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
        {index + 1} / {images.length}
      </div>
    </div>
  );
}

function Lightbox({ project, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [loaded, setLoaded] = useState(false);
  const total = project.images.length;

  useEffect(() => setIdx(startIndex), [startIndex]);

  useEffect(() => {
    project.images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [project.images]);

  useEffect(() => {
    setLoaded(false);
  }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % total);
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + total) % total);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [total, onClose]);

  const goPrev = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i - 1 + total) % total);
  };
  const goNext = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i + 1) % total);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/90 px-3 py-16 backdrop-blur-md md:px-10 md:py-12"
      role="dialog"
      aria-modal="true"
      aria-label={`${project.title} screenshots`}
    >
      <div
        className="absolute left-4 top-4 z-10 inline-flex max-w-[60%] items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white backdrop-blur md:left-7 md:top-7"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-accent" />
        <span className="truncate">{project.title}</span>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20 md:right-7 md:top-7"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
          <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div
        className="relative flex h-full w-full max-w-6xl flex-1 items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {!loaded && (
          <div className="absolute inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            Loading
          </div>
        )}

        <AnimatePresence mode="popLayout" initial={false}>
          <motion.img
            key={idx}
            src={project.images[idx]}
            alt={`${project.title} screen ${idx + 1} of ${total}`}
            onLoad={() => setLoaded(true)}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: loaded ? 1 : 0, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[78vh] max-w-full rounded-xl border border-white/15 object-contain shadow-2xl md:rounded-2xl md:max-h-[82vh]"
            draggable={false}
          />
        </AnimatePresence>
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous image"
            className="absolute left-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20 md:left-7 md:h-12 md:w-12"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next image"
            className="absolute right-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20 md:right-7 md:h-12 md:w-12"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      <div
        className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-white backdrop-blur md:bottom-7"
        onClick={(e) => e.stopPropagation()}
      >
        {total > 1 && (
          <div className="flex items-center gap-1.5">
            {project.images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show screen ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-400 ${
                  i === idx ? 'w-7 bg-white' : 'w-1.5 bg-white/45 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/80">
          {idx + 1} / {total}
        </span>
      </div>
    </motion.div>
  );
}

function Projects({ projects }) {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="work" className="section-space bg-snow/70" data-aos="fade-up">
      <div className="section-wrap">
        <div className="mb-14 flex flex-col justify-between gap-8 md:mb-20 md:flex-row md:items-end">
          <div>
            <p className="meta-label">03 / Selected Work</p>
            <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.86] tracking-[-0.03em] md:text-7xl">
              Selected
              <br />
              Projects
            </h2>
          </div>
          <p className="max-w-xs text-sm uppercase tracking-[0.2em] text-ink/50 md:text-right">
            Freelance work shipped for real clients — premium design and full stack engineering.
          </p>
        </div>

        <div className="flex flex-col gap-14 md:gap-24">
          {projects.map((project, index) => {
            const reverse = index % 2 === 1;
            return (
              <motion.article
                key={project.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.72, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="group grid grid-cols-1 items-center gap-5 md:grid-cols-12 md:gap-12"
              >
                <button
                  type="button"
                  onClick={() => setLightbox({ project, startIndex: 0 })}
                  aria-label={`View ${project.title} screenshots`}
                  className={`group/zoom relative block w-full text-left md:col-span-8 ${reverse ? 'md:order-2' : ''}`}
                >
                  <ProjectGallery images={project.images} title={project.title} />
                  <span className="pointer-events-none absolute right-3 bottom-3 z-[4] inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-black/55 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur transition-opacity duration-300 md:opacity-0 md:group-hover/zoom:opacity-100">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" />
                      <path strokeLinecap="round" d="M21 21l-4.3-4.3M8 11h6M11 8v6" />
                    </svg>
                    View full
                  </span>
                </button>

                <motion.div
                  initial={{ opacity: 0, x: reverse ? -24 : 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`md:col-span-4 ${reverse ? 'md:order-1' : ''}`}
                >
                  <p className="meta-label">{`0${index + 1} / Case Study`}</p>
                  <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-[0.95] tracking-[-0.02em] sm:text-3xl md:text-4xl">
                    {project.title}
                  </h3>
                  {project.client && (
                    <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-accent md:text-[11px] md:tracking-[0.24em]">
                      {project.client}
                    </p>
                  )}
                  <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ink/50 md:tracking-[0.22em]">{project.category}</p>

                  <p className="copy-refined mt-4 text-sm text-ink/74 md:mt-5">{project.summary}</p>

                  {project.tech?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5 md:mt-5 md:gap-2">
                      {project.tech.map((t) => (
                        <span
                          key={t}
                          className="ui-chip rounded-full border border-ink/15 bg-snow/55 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/70 md:px-3 md:tracking-[0.18em]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink/8 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink md:mt-6 md:tracking-[0.2em]">
                    <span className="accent-dot" />
                    {project.resultTag}
                  </div>

                  <a
                    href={project.href}
                    className="ui-nav-link mt-6 inline-flex w-full items-center justify-between gap-2 rounded-full border border-ink/15 bg-snow/60 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink md:mt-7 md:w-auto md:border-0 md:bg-transparent md:p-0 md:text-sm"
                  >
                    Discuss a similar build
                    <span className="ui-arrow-hover text-accent">↗</span>
                  </a>
                </motion.div>
              </motion.article>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            key={lightbox.project.id}
            project={lightbox.project}
            startIndex={lightbox.startIndex}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export default Projects;
